<template>
  <label
    :class="[
      'inline-flex items-center gap-2 select-none',
      props.disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
    ]"
  >
    <input
      v-if="typeof value !== 'boolean'"
      type='checkbox'
      v-model='isChecked'
      :disabled='props.disabled'
      class='hidden'
    />

    <div
      :class="[
        'flex items-center justify-center rounded-md border duration-200 ease-in-out',
        {
          'border-neutral-500 bg-transparent': !isChecked,
          'ui-checkbox': isChecked,
          'w-3 h-3': getSize === 'small',
          'w-5 h-5': getSize === 'medium',
          'w-6 h-6': getSize === 'large'
        }
      ]"
    >
      <UiIcon
        v-if='isChecked'
        icon='check'
        :size='{ small: 8, medium: 14, large: 18 }[getSize]'
        class='text-white dark:text-black'
      />
    </div>

    <span v-if='props.label'>
      {{ props.label }}
    </span>
  </label>
</template>

<script setup lang='ts'>
import { computed } from 'vue'
import UiIcon from '@/components/ui/icon.vue'

const props = defineProps<{
  disabled?: boolean
  size?: 'small' | 'medium' | 'large'
  label?: string
  value?: boolean | string
  checked?: boolean | undefined
}>()

const model = defineModel<boolean | string[]>({
  required: false
})

const isChecked = computed({
  get: () => {
    if (props.checked)
      return true

    if (typeof model.value === 'boolean')
      return model.value

    // Modelo nulo não é array nem boolean: acontece com campo anulável da API que chega como
    // null ao editar um registro nunca configurado. Antes disto a leitura estourava e derrubava
    // a tela inteira — um checkbox sem valor é, simplesmente, um checkbox desmarcado.
    if (!Array.isArray(model.value))
      return !!model.value

    return model.value.includes(props.value as string)
  },
  set: (val) => {
    if (!props.disabled) {
      if (typeof props.value === 'string') {
        const arr = Array.isArray(model.value) ? [...(model.value as string[])] : []
        const idx = arr.indexOf(props.value)
        if (val && idx === -1) {
          arr.push(props.value)
        } else if (!val && idx !== -1) {
          arr.splice(idx, 1)
        }
        model.value = arr
      } else {
        model.value = val
      }
    }
  }
})

const getSize = computed(() => {
  const sz = (props.size || '').toLowerCase()
  if (['small', 'medium', 'large'].includes(sz)) return sz
  return 'medium'
})
</script>

<style lang="scss">
.ui-checkbox {
  @apply bg-[--c-primary-600] hover:bg-[--c-primary-500] border-[--c-primary-600];
}
</style>
